import axios from "axios";
import { getTimeUntilExpiry, isTokenExpired } from "../utils/tokenUtils";
import { toast } from "react-toastify";

interface TokenRefreshEvent {
  type: "TOKEN_REFRESH_SUCCESS" | "TOKEN_REFRESH_FAILED";
  tokens?: any;
}

class TokenManager {
  private isRefreshing = false;
  private eventListeners: ((event: TokenRefreshEvent) => void)[] = [];
  private refreshTimer: any = null;
  private REFRESH_BUFFER_TIME = 30 * 60 * 1000; // 30 minutes in ms
  private JITTER_RANGE = 5 * 60 * 1000; // 5 minutes in ms

  addEventListener(callback: (event: TokenRefreshEvent) => void) {
    this.eventListeners.push(callback);
  }

  removeEventListener(callback: (event: TokenRefreshEvent) => void) {
    this.eventListeners = this.eventListeners.filter(
      (listener) => listener !== callback,
    );
  }

  private emitEvent(event: TokenRefreshEvent) {
    this.eventListeners.forEach((listener) => listener(event));
  }

  initialize() {
    this.setupAutoRefresh();
  }

  private setupAutoRefresh() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const accessToken = user?.access_token;

    if (!accessToken) {
      return;
    }

    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (isTokenExpired(accessToken)) {
      // Token is already expired, emit failure event
      this.emitEvent({ type: "TOKEN_REFRESH_FAILED" });
      return;
    }

    const timeUntilExpiry = getTimeUntilExpiry(accessToken);

    if (timeUntilExpiry === null) {
      console.error("Could not determine token expiry time");
      return;
    }

    // Calculate refresh time with 30-minute buffer and ±5 minute jitter
    const jitter = (Math.random() - 0.5) * 2 * this.JITTER_RANGE; // -JITTER_RANGE to +JITTER_RANGE
    const refreshTime = Math.max(
      0,
      timeUntilExpiry * 1000 - this.REFRESH_BUFFER_TIME + jitter,
    );

    this.refreshTimer = setTimeout(async () => {
      await this.refreshToken();
    }, refreshTime);
  }

  private async refreshToken() {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const refreshToken = user?.refreshToken;
      const userId = user?.user?.id;

      if (!refreshToken || !userId) {
        throw new Error("No refresh token or user ID available");
      }

      const baseUrl = process.env.REACT_APP_SERVER_URL?.replace(/\/$/, ""); // Remove trailing slash
      const apiUrl = `${baseUrl}/v1/auth/refresh_token`;

      const response = await axios.post(apiUrl, {
        oldRefreshToken: refreshToken,
        userId,
      });

      // Update localStorage with new tokens (flat structure)
      const updatedUser = {
        ...user,
        access_token: response.data.data.access_token,
        refreshToken: response.data.data.refreshToken,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Emit success event with new tokens (flat structure)
      this.emitEvent({
        type: "TOKEN_REFRESH_SUCCESS",
        tokens: {
          access_token: response.data.data.access_token,
          refreshToken: response.data.data.refreshToken,
        },
      });

      // Setup next refresh
      this.setupAutoRefresh();
    } catch (error: any) {
      console.error("Token refresh failed:", error.message);
      toast.error("Session expired. Please log in again.");
      this.emitEvent({ type: "TOKEN_REFRESH_FAILED" });
    } finally {
      this.isRefreshing = false;
    }
  }

  clearTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Call this when user logs in
  onLogin() {
    this.setupAutoRefresh();
  }

  // Call this when user logs out
  onLogout() {
    this.clearTimer();
  }
}

export const tokenManager = new TokenManager();
